import { editor } from 'monaco-editor';

export const MAIN_PROGRAM_FILE_NAME = 'Main.py';

export interface MainProgramEditOptions {
  undoStop?: boolean;
  focusEditor?: boolean;
}

export interface BpsMainProgramEditor extends editor.IStandaloneCodeEditor {
  getMainProgramModel?: () => editor.ITextModel | null | undefined;
  getMainProgramCode?: () => string | null | undefined;
  showMainProgram?: () => boolean;
  applyMainProgramEdits?: (source: string, edits: editor.IIdentifiedSingleEditOperation[], options?: MainProgramEditOptions) => boolean;
}

function asMainProgramEditor(ed?: editor.IStandaloneCodeEditor | null): BpsMainProgramEditor | undefined {
  return ed as BpsMainProgramEditor | undefined;
}

export function getMainProgramModel(ed?: editor.IStandaloneCodeEditor | null): editor.ITextModel | null {
  const mainEditor = asMainProgramEditor(ed);
  return mainEditor?.getMainProgramModel?.() ?? ed?.getModel() ?? null;
}

export function getMainProgramCode(ed?: editor.IStandaloneCodeEditor | null, fallback = ''): string {
  const mainEditor = asMainProgramEditor(ed);
  const code = mainEditor?.getMainProgramCode?.();
  if (code != null) return code;
  return getMainProgramModel(ed)?.getValue() ?? fallback;
}

/** True when the editor instance is still mounted and its Main.py model is readable. */
export function isMainProgramEditorMounted(ed?: editor.IStandaloneCodeEditor | null): boolean {
  if (!ed) return false;

  try {
    if (typeof ed.getDomNode === 'function' && !ed.getDomNode()) {
      return false;
    }

    const model = getMainProgramModel(ed);
    if (model?.isDisposed?.()) {
      return false;
    }

    if (model) {
      return true;
    }

    return typeof ed.getDomNode === 'function' ? Boolean(ed.getDomNode()) : false;
  } catch {
    return false;
  }
}

/**
 * Program text for deploy/download actions. Uses the live Monaco Main.py buffer when the editor
 * is mounted; otherwise falls back to Redux project code (e.g. after auto-swap to dashboard).
 */
export function resolveDeployProgramCode(fallback = ''): string {
  const mountedEditor = typeof window !== 'undefined' ? window.__bps_editor : undefined;
  if (!isMainProgramEditorMounted(mountedEditor)) {
    return fallback;
  }

  return getMainProgramCode(mountedEditor, fallback);
}

/** Clears the global editor reference when the mounted editor unmounts. */
export function clearMountedMainProgramEditor(ed?: editor.IStandaloneCodeEditor | null): void {
  if (typeof window === 'undefined' || !ed) return;
  if (window.__bps_editor === ed) {
    window.__bps_editor = null;
  }
}

export function showMainProgram(ed?: editor.IStandaloneCodeEditor | null): boolean {
  const mainEditor = asMainProgramEditor(ed);
  return mainEditor?.showMainProgram?.() ?? Boolean(ed?.getModel());
}

export function applyMainProgramEdits(
  ed: editor.IStandaloneCodeEditor | undefined | null,
  source: string,
  edits: editor.IIdentifiedSingleEditOperation[],
  options: MainProgramEditOptions = {}
): boolean {
  if (!ed || edits.length === 0) return false;

  const mainEditor = asMainProgramEditor(ed);
  if (mainEditor?.applyMainProgramEdits) {
    return mainEditor.applyMainProgramEdits(source, edits, options);
  }

  const shouldPushUndoStop = options.undoStop !== false;
  if (shouldPushUndoStop) {
    ed.pushUndoStop();
  }
  ed.executeEdits(source, edits);
  if (shouldPushUndoStop) {
    ed.pushUndoStop();
  }
  if (options.focusEditor !== false) {
    ed.focus();
  }
  return true;
}

export function getMainProgramEditorProxy(ed?: editor.IStandaloneCodeEditor | null): editor.IStandaloneCodeEditor | undefined {
  if (!ed) return undefined;

  const mainEditor = asMainProgramEditor(ed);
  if (!mainEditor?.getMainProgramModel && !mainEditor?.applyMainProgramEdits) {
    return ed;
  }

  return new Proxy(ed, {
    get(target, prop, receiver) {
      if (prop === 'getModel') {
        return () => getMainProgramModel(target);
      }
      if (prop === 'pushUndoStop') {
        return () => {
          showMainProgram(target);
          return target.pushUndoStop();
        };
      }
      if (prop === 'executeEdits') {
        return (source: string, edits: editor.IIdentifiedSingleEditOperation[]) =>
          applyMainProgramEdits(target, source, edits, { undoStop: false, focusEditor: false });
      }
      if (prop === 'focus') {
        return () => {
          showMainProgram(target);
          return target.focus();
        };
      }

      return Reflect.get(target, prop, receiver);
    },
  }) as editor.IStandaloneCodeEditor;
}
